import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import images from '../../components/images';

function ResetPassword() {
  const [showPassword, setShowPassword] = useState(false);
const [showConPassword, setShowConPassword] = useState(false); // 👈 ADD THIS LINE
  const [loading, setLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const { validateResetToken, resetPassword } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  const hasValidated = useRef(false); // ✅ Prevent re-validation on re-renders

useEffect(() => {
  const checkToken = async () => {
    if (!token) {
      toast.error('Invalid or missing token.');
      navigate('/forget-password');
      return;
    }

    setLoading(true);
    try {
      const response = await validateResetToken(token);
      if (response?.success) {
        setValidToken(true);
      } else {
        setValidToken(false);
        setErrorMessage(response?.message || 'Invalid or expired token.');
      }
    } catch (err) {
      setValidToken(false);
      setErrorMessage(err.message || 'Invalid or expired token.');
    } finally {
      setLoading(false);
    }
  };

  checkToken();
}, [token, navigate, validateResetToken]); // validateResetToken is stable now

  const onSubmit = async (data) => {
    setErrorMessage('');
    setLoading(true);

    try {
      await resetPassword({ token, password: data.password });
      toast.success('Password reset successful! Please log in.');
      navigate('/login');
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to reset password';
      setErrorMessage(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="text-center p-6">Validating token...</div>;
  }

  if (!validToken) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-[40px] max-w-[640px] w-full bg-white rounded-[20px] shadow text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
          <p className="text-gray-700">{errorMessage || 'This reset link is invalid or has expired.'}</p>
          <Link
            to="/forget-password"
            className="mt-6 inline-block bg-brandBlue text-white py-2 px-4 rounded hover:bg-blue-600"
          >
            Request a New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="fixed top-0 left-0 w-full bg-brandBlue border-b-8 border-brandYellow py-5 z-10"></div>

      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-[80px] max-w-[640px] w-full space-y-8 bg-white rounded-[20px] shadow">
          <div className="flex justify-center mb-6">
            <img src={images.logo} alt="Future Forward Logo" className="w-100" />
          </div>

          <h2 className="text-center text-2xl font-bold text-gray-900">Reset Password</h2>

          <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div className="space-y-4">
             <div className="relative">
    <label htmlFor="password" className="block text-[#717576] text-base font-normal">
        New Password
    </label>

    <div className="relative mt-1"> 
        <input
            {...register('password', { /* ... */ })}
            type={showPassword ? 'text' : 'password'}
            placeholder="Enter new password"
            className="input rounded-[10px] py-[17px] px-4 bg-[#7175760F] w-full focus:outline-none"
        />
        <div
            // Now, top-1/2 -translate-y-1/2 works perfectly as it's relative to the input!
            className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer"
            onClick={() => setShowPassword(!showPassword)}
        >
             {showPassword ? (
                    <EyeIcon className="h-5 w-5 text-gray-400" />

                  ) : (
                                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />

                  )}
        </div>
    </div>
            {errorMessage && <p className="text-red-600 text-center">{errorMessage}</p>}

    {/* ... error message div (position fixed) ... */}
</div>

            
            <div className="relative">
        <label htmlFor="confirmPassword" className="block text-[#717576] text-base font-normal">
            Confirm Password
        </label>
        
        {/* 🛑 FIX: Remove the outer onClick handler and the input's incorrect 'mt-1' class */}
        <div className="relative mt-1"> 
            <input
                {...register('confirmPassword', {
                    required: 'Please confirm your password',
                    validate: (value) => value === watch('password') || 'Passwords do not match',
                })}
               
                type={showConPassword ? 'text' : 'password'}
                placeholder="Confirm new password"
                
                // 🛑 FIX: Remove the redundant 'mt-1' class on the input, as the parent 'div.relative' already has it.
                className="input rounded-[10px] py-[17px] px-4 bg-[#7175760F] w-full focus:outline-none"
            />
            
            {/* 🛑 FIX: Wrap the icons in the absolute positioned container with the correct click handler */}
            <div 
                className="absolute right-4 top-1/2 -translate-y-1/2 cursor-pointer" 
                onClick={() => setShowConPassword(!showConPassword)}
            >
                {/* 🛑 FIX: Icon logic must check the new state variable */}
                {showConPassword ? (
                    <EyeIcon className="h-5 w-5 text-gray-400" />
                ) : (
                    <EyeSlashIcon className="h-5 w-5 text-gray-400" />

                )}
            </div>
        </div>
        
        {errors.confirmPassword && (
            <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
        )}
    </div>
             
            </div>

            {errorMessage && <p className="text-red-600 text-center">{errorMessage}</p>}

            <div className="pt-[1.5rem]">
              <button
                type="submit"
                disabled={loading}
                className="btn-primary bg-brandBlue w-full py-[17px] px-4 text-base"
              >
                {loading ? 'Processing...' : 'Reset Password'}
              </button>
            </div>

            <div className="text-center pt-[1.5rem]">
              <Link to="/login" className="text-[15px] text-brandBlue hover:text-primary-500">
                Back to Login
              </Link>
            </div>
          </form>
        </div>
      </div>

      <div className="fixed bottom-0 left-0 w-full bg-brandBlue border-t-8 border-brandYellow py-5 z-10"></div>
    </>
  );
}

export default ResetPassword;
